import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { errorToast, infoToast } from 'configuration/notifications';

import { rentExitSelector, accrualListSelector, rentSelector } from 'store/transferAct/selectors';
import { stockSelector, stockListSelector } from 'store/stock/selectors';
import { Client } from 'store/client/actions';
import { Stock } from 'store/stock/actions';
import { RentExit, Rent } from 'store/transferAct/actions';

import { ActionButton } from 'components/ActionButton';
import { InputAutoComplete } from 'components/InputAutoComplete/InputAutoComplete';
import { getUUID } from 'helpers/utils';

const ActionButtonsSection = () => {
  const dispatch = useDispatch();
  const rent = useSelector(rentExitSelector);
  const rentMain = useSelector(rentSelector);

  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const accrualSubTypes = useSelector(accrualListSelector);

  const [client, setClient] = useState({ open: false, key: 'client', data: {} });
  const [instruments, setInstruments] = useState({ open: false, key: 'instruments', data: [] });
  const [comment, setComment] = useState({ open: false, key: 'comment', data: '' });
  const [address, setAddress] = useState({ open: false, key: 'address', data: '' });
  const [bonus, setBonus] = useState({ open: false, key: 'bonusSpent', data: 0 });
  const [agr, setAgr] = useState({ open: false, key: 'agr', data: { value: null, selected: null } });
  const [fine, setFine] = useState({ open: false, key: 'fine', data: { selector: null, price: 0, comment: '' } });
  const [discount, setDiscount] = useState({
    open: false,
    key: 'discount',
    data: { selector: null, price: 0, comment: '' },
  });

  useEffect(() => {
    dispatch(Rent.getAccrualList());
    dispatch(Client.getOrganizations());
  }, []);

  useEffect(() => {
    if (rent) {
      setClient({ ...client, data: rent?.client });
      setInstruments({ ...instruments, data: rent?.instruments });
      setComment({ ...comment, data: rent?.comment || '' });
      setAddress({ ...address, data: rent?.address || '' });
      setBonus({ ...bonus, data: rent?.bonusSpent || 0 });

      setAgr({
        ...agr,
        data: {
          value: rent?.agr || null,
        },
      });

      if (rent?.client) {
        dispatch(Client.getAgreements(rent.client.id));
      } else {
        dispatch(Client.setAgreementsList([]));
      }
    }
  }, [rent]);

  const handleCloseButton = () => {
    setComment({ ...comment, open: false });
    setAddress({ ...address, open: false });
    setBonus({ ...bonus, open: false });
    setAgr({ ...agr, open: false });
    setFine((state) => ({ ...state, open: false }));
    setDiscount((state) => ({ ...state, open: false }));
  };

  const handleChange = (target) => {
    const newRent = {};

    switch (target.key) {
      case 'client':
        if (target.data && target.data.isTemporary) {
          infoToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
        }

        newRent.client = target.data;
        newRent.agr = null;
        newRent.bonusSpent = 0;
        break;

      case 'agr':
        newRent.agr = target.data;
        break;

      case 'bonusSpent':
        newRent.bonusSpent = target.data;
        break;

      case 'comment':
        newRent.comment = target.data;
        break;

      case 'address':
        newRent.address = target.data;
        break;

      case 'fine':
        newRent.fine = [
          ...rent.fine,
          {
            sum: target.data.price,
            comment: target.data.comment,
            accrualSubType: target.data.selector,
            fronEndID: getUUID(),
          },
        ];
        setFine({
          open: false,
          key: 'fine',
          data: { selector: null, price: 0, comment: '' },
        });
        break;

      case 'discount':
        newRent.discount = [
          ...rent.discount,
          {
            sum: target.data.price,
            comment: target.data.comment,
            accrualSubType: target.data.selector,
            fronEndID: getUUID(),
          },
        ];
        setDiscount({
          open: false,
          key: 'discount',
          data: { selector: null, price: 0, comment: '' },
        });
        break;

      case 'instruments':
        if (!rent.instruments.find((inst) => inst.id === target.data.id)) {
          if (target.data.stockID !== stock.stockID1C && target.data.stockID !== '') {
            if (rent.instruments.length === 0 || stock.stockName === 'Доставка') {
              const newStock = stockList.find((stock) => stock.stockID1C === target.data.stockID);

              if (newStock) {
                dispatch(Stock.setStock(newStock));
                newRent.stockID = newStock.stockID1C;
              }
            } else {
              errorToast('Нельзя брать инструменты из разных складов!');
              return;
            }
          }

          const newChosenItem = {
            ...target.data,
            quantity: 1,
            rentTerm: 1,
          };

          if (newChosenItem.is_priced_by_wear) {
            newChosenItem.quantity = 0;
            newChosenItem.rentTerm = 0;
          }

          newRent.instruments = [...rent.instruments, { ...newChosenItem }];
        }
        break;

      default:
        return;
    }

    handleCloseButton();
    dispatch(RentExit.updateRent(newRent));
  };

  const handleValidateAccrual = (accrual) => {
    if (!accrual.data.selector?.id) {
      errorToast('Нужно выбрать обоснование!');
      return;
    }

    if (accrual.data.price <= 0) {
      errorToast('Неверная сумма!');
      return;
    }
    handleChange(accrual);
  };

  const setCommentValue = (value) => {
    setComment({ ...comment, data: value });
  };

  const setDiscountValue = (target, value) => {
    setDiscount({ ...discount, data: { ...discount.data, [target]: value } });
  };

  const setFineValue = (target, value) => {
    setFine({ ...fine, data: { ...fine.data, [target]: value } });
  };

  return (
    <div className="rent-section">
      <ActionButton
        text="Скидка"
        closeText="Добавить"
        size="-90px"
        onSubmit={(e) => handleValidateAccrual(discount)}
        isOpen={discount.open}
        onClick={(open) => setDiscount({ ...discount, open })}
        icon="discount-btn"
      >
        <div className="modal-body">
          <InputAutoComplete
            icon="search"
            onChange={(selector) => setDiscountValue('selector', selector)}
            list={accrualSubTypes
              .filter((subtype) => subtype.category.includes('Знижки'))
              .map((subtype) => ({
                value: subtype.name,
                text: subtype.name,
                returning: subtype,
              }))}
            selected={
              discount.data.selector ? { text: discount.data.selector.name, value: discount.data.selector.name } : null
            }
            placeholder="Обоснование"
            type="text"
            size="none"
          />

          <div className="modal-body-date margin-t10 flex-center">
            <InputButton
              value={discount.data.price || 0}
              onChange={(price) => setDiscountValue('price', price)}
              className="input-property"
              type="number"
              style={{ marginRight: '5px', flex: 1 }}
            />
            грн.
            <button
              onClick={() => setDiscountValue('price', '50')}
              className="btn-property"
              style={{ margin: '0 3px' }}
            >
              50
            </button>
            <button
              onClick={() => setDiscountValue('price', '100')}
              className="btn-property"
              style={{ margin: '0 3px' }}
            >
              100
            </button>
          </div>
          <TextAreaButton
            value={discount.data.comment || ''}
            onChange={(comment) => setDiscountValue('comment', comment)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>

      <ActionButton
        text="Штраф"
        closeText="Добавить"
        size="-90px"
        onSubmit={(e) => handleValidateAccrual(fine)}
        isOpen={fine.open}
        onClick={(open) => setFine({ ...fine, open })}
        icon="fine-btn"
      >
        <div className="modal-body">
          <InputAutoComplete
            icon="search"
            onChange={(selector) => setFineValue('selector', selector)}
            list={accrualSubTypes
              .filter((subtype) => subtype.category.includes('Додаткові нарахування'))
              .map((subtype) => ({
                value: subtype.name,
                text: subtype.name,
                returning: subtype,
              }))}
            selected={fine.data.selector ? { text: fine.data.selector.name, value: fine.data.selector.name } : null}
            placeholder="Обоснование"
            type="text"
            size="auto"
          />

          <div className="modal-body-date margin-t10 flex-center">
            <InputButton
              value={fine.data.price || 0}
              onChange={(price) => setFineValue('price', price)}
              className="input-property"
              type="number"
              style={{ marginRight: '5px', flex: 1 }}
            />
            грн.
            <button onClick={() => setFineValue('price', '50')} className="btn-property" style={{ margin: '0 3px' }}>
              50
            </button>
            <button onClick={() => setFineValue('price', '100')} className="btn-property" style={{ margin: '0 3px' }}>
              100
            </button>
          </div>
          <TextAreaButton
            value={fine.data.comment || ''}
            onChange={(comment) => setFineValue('comment', comment)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>

      <ActionButton
        text="Комментарий"
        closeText="Добавить"
        size="-70px"
        onSubmit={(e) => handleChange(comment)}
        isOpen={comment.open}
        onClick={(open) => setComment({ ...comment, open })}
        icon="comment-btn"
      >
        <div className="modal-body">
          <TextAreaButton
            value={comment.data || ''}
            onChange={(value) => setCommentValue(value)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>
    </div>
  );
};

const TextAreaButton = ({ onChange, ...props }) => {
  return (
    <textarea
      type="text"
      className="modal-textarea"
      {...props}
      onChange={({ target: { value } }) => onChange(value)}
    ></textarea>
  );
};

TextAreaButton.propTypes = {
  onChange: PropTypes.func,
};

const InputButton = ({ onChange, ...props }) => {
  return <input {...props} onChange={({ target: { value } }) => onChange(value)}></input>;
};

InputButton.propTypes = {
  onChange: PropTypes.func,
};

export { ActionButtonsSection };
